// Coordinate parsing and conversion utilities for Canadian aviation standards

export interface ParsedCoordinate {
  decimal: number;
  dms: string; // Degrees Minutes Seconds format
  aviation: string; // Canadian aviation format
}

export interface CoordinatePair {
  latitude: ParsedCoordinate;
  longitude: ParsedCoordinate;
}

// Normalize the assorted Unicode characters real input contains — smart
// quotes, the prime/double-prime aviation uses for minutes/seconds, and
// degree look-alikes — to the plain ASCII the format regexes expect.
// Without this, "50° 37.902′N" (prime) or curly-quote input fails to parse.
function normalizeCoordinateChars(input: string): string {
  return input
    .replace(/[′’‘´`]/g, "'") // ′ ' ' ´ ` → '
    .replace(/[″“”]/g, '"')             // ″ " " → "
    .replace(/[º⁰]/g, '°');             // º ⁰ → °
}

// Parse various coordinate formats
export function parseCoordinate(input: string, isLatitude: boolean): ParsedCoordinate | null {
  if (!input || typeof input !== 'string') return null;

  const cleaned = normalizeCoordinateChars(input.trim());
  
  // Try to parse different formats
  let decimal: number | null = null;
  
  // 1. Decimal degrees, optionally with a degree symbol and/or hemisphere
  //    letter (e.g. 49.2827, -123.1207, "50.63170° N", "123.504 W")
  const decimalMatch = cleaned.match(/^(-?\d+\.?\d*)\s*°?\s*([NSEW]?)$/i);
  if (decimalMatch) {
    decimal = parseFloat(decimalMatch[1]);
    const dir = decimalMatch[2].toUpperCase();
    if (dir === 'S' || dir === 'W') decimal = -Math.abs(decimal);
    else if (dir === 'N' || dir === 'E') decimal = Math.abs(decimal);
  }
  
  // 2. DMS format (e.g., 49°16'58"N, 123°07'15"W)
  const dmsMatch = cleaned.match(/^(\d+)[°\s]+(\d+)['\s]+(\d+\.?\d*)["\s]*([NSEW]?)$/i);
  if (dmsMatch && !decimal) {
    const degrees = parseInt(dmsMatch[1]);
    const minutes = parseInt(dmsMatch[2]);
    const seconds = parseFloat(dmsMatch[3]);
    const direction = dmsMatch[4].toUpperCase();
    
    decimal = degrees + minutes / 60 + seconds / 3600;
    
    if (direction === 'S' || direction === 'W') {
      decimal = -decimal;
    }
  }
  
  // 3. Degrees and decimal minutes (e.g., 49° 16.967' N)
  const dmMatch = cleaned.match(/^(\d+)[°\s]+(\d+\.?\d*)['\s]*([NSEW]?)$/i);
  if (dmMatch && !decimal) {
    const degrees = parseInt(dmMatch[1]);
    const minutes = parseFloat(dmMatch[2]);
    const direction = dmMatch[3].toUpperCase();
    
    decimal = degrees + minutes / 60;
    
    if (direction === 'S' || direction === 'W') {
      decimal = -decimal;
    }
  }
  
  // 4. NMEA format (e.g., 4916.967,N or 12307.250,W)
  const nmeaMatch = cleaned.match(/^(\d{2,3})(\d{2}\.\d+),([NSEW])$/i);
  if (nmeaMatch && !decimal) {
    const degrees = parseInt(nmeaMatch[1]);
    const minutes = parseFloat(nmeaMatch[2]);
    const direction = nmeaMatch[3].toUpperCase();
    
    decimal = degrees + minutes / 60;
    
    if (direction === 'S' || direction === 'W') {
      decimal = -decimal;
    }
  }
  
  if (decimal === null) return null;
  
  // Validate ranges
  if (isLatitude && (decimal < -90 || decimal > 90)) return null;
  if (!isLatitude && (decimal < -180 || decimal > 180)) return null;

  // BC-specific: if parsing longitude and it's positive in the 114-139 range,
  // the user likely forgot the negative sign (BC is in the western hemisphere).
  // We auto-correct this to negative since all of Canada is west of the prime meridian.
  if (!isLatitude && decimal > 0 && decimal >= 114 && decimal <= 180) {
    decimal = -decimal;
  }
  
  // Convert to standard formats
  const dms = decimalToDMS(decimal, isLatitude);
  const aviation = decimalToAviation(decimal, isLatitude);
  
  return {
    decimal,
    dms,
    aviation
  };
}

// Convert decimal degrees to DMS format
function decimalToDMS(decimal: number, isLatitude: boolean): string {
  const absolute = Math.abs(decimal);
  const degrees = Math.floor(absolute);
  const minutesDecimal = (absolute - degrees) * 60;
  const minutes = Math.floor(minutesDecimal);
  const seconds = Math.round((minutesDecimal - minutes) * 60 * 100) / 100;
  
  const direction = isLatitude 
    ? (decimal >= 0 ? 'N' : 'S')
    : (decimal >= 0 ? 'E' : 'W');
  
  return `${degrees}°${minutes.toString().padStart(2, '0')}'${seconds.toFixed(2).padStart(5, '0')}"${direction}`;
}

// Convert to Canadian aviation format (degrees and decimal minutes)
function decimalToAviation(decimal: number, isLatitude: boolean): string {
  const absolute = Math.abs(decimal);
  const degrees = Math.floor(absolute);
  const minutes = (absolute - degrees) * 60;
  
  const direction = isLatitude 
    ? (decimal >= 0 ? 'N' : 'S')
    : (decimal >= 0 ? 'E' : 'W');
  
  // Format: DD°MM.mmm'N/S or DDD°MM.mmm'E/W
  const degStr = isLatitude 
    ? degrees.toString().padStart(2, '0')
    : degrees.toString().padStart(3, '0');
  
  const minStr = minutes.toFixed(3).padStart(6, '0');
  
  return `${degStr}°${minStr}'${direction}`;
}

// Parse a coordinate pair (latitude, longitude)
export function parseCoordinatePair(latInput: string, lonInput: string): CoordinatePair | null {
  const latitude = parseCoordinate(latInput, true);
  const longitude = parseCoordinate(lonInput, false);

  if (!latitude || !longitude) return null;

  return { latitude, longitude };
}

// Parse a single free-form "lat, lon" string in any supported format:
// comma-separated, space-separated, or DMS with internal spaces
// (e.g. `48°48'50"N 123°38'56"W`). Returns null if it can't be understood.
export function parseCoordinateInput(input: string): CoordinatePair | null {
  const cleaned = (input || '').trim();
  if (!cleaned) return null;

  // Try comma-separated first: "48.813826, -123.648911"
  let parts = cleaned.split(',').map(s => s.trim()).filter(s => s.length > 0);
  if (parts.length === 2) {
    const pair = parseCoordinatePair(parts[0], parts[1]);
    if (pair) return pair;
  }

  // Try space-separated: "48.813826 -123.648911"
  parts = cleaned.split(/\s+/).filter(s => s.length > 0);
  if (parts.length === 2) {
    const pair = parseCoordinatePair(parts[0], parts[1]);
    if (pair) return pair;
  }

  // DMS with internal spaces: split the tokens down the middle
  if (parts.length > 2) {
    const mid = Math.floor(parts.length / 2);
    const pair = parseCoordinatePair(parts.slice(0, mid).join(' '), parts.slice(mid).join(' '));
    if (pair) return pair;
  }

  return null;
}

// Format coordinates for display
export function formatCoordinates(lat: number | null, lon: number | null, format: 'decimal' | 'dms' | 'aviation' = 'aviation'): string {
  if (lat === null || lon === null) return '';
  
  const latCoord = parseCoordinate(lat.toString(), true);
  const lonCoord = parseCoordinate(lon.toString(), false);
  
  if (!latCoord || !lonCoord) return '';
  
  switch (format) {
    case 'decimal':
      return `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
    case 'dms':
      return `${latCoord.dms} ${lonCoord.dms}`;
    case 'aviation':
      return `${latCoord.aviation} ${lonCoord.aviation}`;
    default:
      return `${latCoord.aviation} ${lonCoord.aviation}`;
  }
}

// Validate What3Words format
export function validateWhat3Words(input: string): boolean {
  if (!input) return false;
  
  // What3words format: word.word.word
  const pattern = /^[a-z]+\.[a-z]+\.[a-z]+$/i;
  return pattern.test(input.trim());
}

// Calculate distance between two coordinates (in kilometers)
export function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

// BC/Western Canada bounding box (with margin for adjacent areas TEAAM might operate)
const BC_BOUNDS = {
  minLat: 48.0,  // Southern BC border
  maxLat: 60.0,  // Northern BC border
  minLon: -139.0, // Western BC (includes Yukon border)
  maxLon: -114.0, // Eastern BC (Alberta border)
};

// Check if coordinates are within BC/Western Canada region
export function isWithinBCRegion(lat: number, lon: number): boolean {
  return lat >= BC_BOUNDS.minLat && lat <= BC_BOUNDS.maxLat &&
         lon >= BC_BOUNDS.minLon && lon <= BC_BOUNDS.maxLon;
}

// Validate coordinates and return warnings (not errors) for suspicious values
export function validateCoordinatesForBC(lat: number, lon: number): {
  valid: boolean;
  warning: string | null;
  suggestion: string | null;
} {
  // Basic range check
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
    return { valid: false, warning: 'Invalid coordinate range', suggestion: null };
  }

  // Check if outside BC region
  if (!isWithinBCRegion(lat, lon)) {
    // Check if swapping would put it in BC (common mistake)
    if (isWithinBCRegion(lon, lat)) {
      return {
        valid: true,
        warning: 'Coordinates appear to be swapped (latitude/longitude reversed)',
        suggestion: `Did you mean ${lon.toFixed(6)}, ${lat.toFixed(6)}?`
      };
    }

    // Check if adding negative to longitude would fix it
    if (lon > 0 && isWithinBCRegion(lat, -lon)) {
      return {
        valid: true,
        warning: 'Longitude should be negative for BC (western hemisphere)',
        suggestion: `Automatically corrected to ${lat.toFixed(6)}, ${(-lon).toFixed(6)}`
      };
    }

    return {
      valid: true,
      warning: `Location is outside BC region (${lat.toFixed(4)}°N, ${Math.abs(lon).toFixed(4)}°${lon < 0 ? 'W' : 'E'})`,
      suggestion: null
    };
  }

  return { valid: true, warning: null, suggestion: null };
}

// Generate a Google Maps URL
export function getGoogleMapsUrl(lat: number, lon: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`;
}

// Generate an OpenStreetMap URL
export function getOpenStreetMapUrl(lat: number, lon: number, zoom: number = 15): string {
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=${zoom}/${lat}/${lon}`;
}